/**
 * 목록 뷰 — 가장 밀집한 표현.
 *
 * **칸으로 묶되 한 줄씩 눕힌다.** 보드는 카드가 옆으로 벌어져 한 화면에
 * 스물몇 장이 한계인데, 목록은 백 장을 훑을 수 있다. 그래서 여기서는
 * 라벨을 그리지 않는다 — 줄이 길어지면 밀집의 이점이 사라진다.
 */

import type { TicketCard } from '../api';
import { COLUMNS } from '../columns';
import { AssigneeMark, BADGE, DueMark, PriorityMark, TypeMark } from './Chips';

export function TicketListView({
  tickets,
  onOpen,
}: {
  tickets: TicketCard[];
  onOpen: (id: number) => void;
}) {
  if (tickets.length === 0) {
    return <p className="page-status">티켓 없음</p>;
  }

  return (
    <div className="flex flex-col gap-s5">
      {COLUMNS.map((def) => {
        const inCol = tickets.filter((item) => item.col === def.id);
        if (inCol.length === 0) return null;
        return (
          <section key={def.id}>
            {/* 목록 뷰의 칸 이름도 보드와 같은 색을 쓴다. 다만 **점 하나만**
                칠한다(2026-10-19) — 칠한 알약은 목록 사이사이에서 카드보다
                먼저 눈에 들어 화면이 지저분해 보였다. */}
            <h2 className="mt-0 mb-s2 flex items-center gap-s2 text-meta font-bold text-ink-2">
              <span aria-hidden="true" className="size-[8px] rounded-full" style={{ background: `var(--st-${def.tone}-f)` }} />
              {def.label}
              <span className="tnum font-medium text-ink-3">{inCol.length}</span>
            </h2>
            <ul className="m-0 list-none overflow-hidden rounded-lg border border-line bg-panel p-0 shadow-panel">
              {inCol.map((ticket) => (
                <li key={ticket.id} className="border-b border-line last:border-b-0">
                  <button
                    type="button"
                    onClick={() => onOpen(ticket.id)}
                    className="flex w-full items-center gap-s3 px-s4 py-s3 text-left hover:bg-sunk max-[720px]:min-h-[44px] max-[720px]:flex-wrap max-[720px]:gap-s2"
                  >
                    {/* **번호가 먼저다** — 카드·표와 같은 차례여야 화면을
                        옮겨 다녀도 눈이 같은 자리를 짚는다. 폭을 고정해
                        제목 시작점이 세로로 곧게 내려간다. */}
                    {/* 휴대폰에서는 **제목이 먼저**고 번호는 아래 줄의 흐린 글자다
                        (2026-10-19) — 칠한 번호표가 제목보다 굵게 읽혔다. */}
                    <span
                      className={[
                        BADGE,
                        'tnum w-[88px] max-[720px]:w-auto max-[720px]:bg-transparent max-[720px]:px-0',
                        ticket.kind === 'issue'
                          ? 'bg-st-red-t font-bold text-st-red-f'
                          : 'bg-sunk text-ink-2 max-[720px]:text-ink-3',
                      ].join(' ')}
                      title={ticket.kind === 'issue' ? '이슈' : '할 일'}
                    >
                      {ticket.key}
                    </span>
                    <TypeMark type={ticket.type} />
                    <span className="min-w-0 flex-1 truncate max-[720px]:order-first max-[720px]:basis-full max-[720px]:whitespace-normal max-[720px]:break-words max-[720px]:font-medium max-[720px]:text-ink max-[720px]:leading-snug">{ticket.title}</span>
                    <PriorityMark priority={ticket.priority} />
                    <DueMark due={ticket.due_date} />
                    <span className="max-[720px]:ml-auto"><AssigneeMark user={ticket.assignee} size={18} /></span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
